"use client";

import Link from "next/link";
import { PiLogo } from "@/components/pi-logo";
import { AuthButton } from "@/components/auth-button";
import { useAuth } from "@/components/auth-provider";
import { useOfferCountdown } from "@/hooks/use-offer-countdown";
import { useStartingPrice } from "@/hooks/use-starting-price";
import { piEvents } from "@/lib/analytics";
import {
  PI_CTA_LOCATION,
  PI_CTA_NAME,
  PI_DESTINATION,
  PI_LOGIN_ENTRY_POINT,
} from "@/lib/analytics/schema";
import { DEFAULT_DISCOUNT_PERCENT, DEFAULT_ORIGINAL_PRICE, HEADER_COPY } from "@/lib/site-copy";

/** One HH/MM/SS box in the urgency countdown. */
function TimeBox({ value, label }: { value: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="min-w-[30px] rounded-md bg-pi-bg px-2 py-0.5 text-center text-sm font-bold tabular-nums text-[#FDF440] sm:text-base">
        {value}
      </span>
      <span className="text-[11px] font-semibold text-[#C9A94A]">{label}</span>
    </span>
  );
}

export function SiteHeader() {
  const startingPrice = useStartingPrice();
  const countdown = useOfferCountdown();
  const { isSignedIn, isAuthResolved } = useAuth();
  // The session cookie is client-only, so the slot stays mounted and only fades — hiding it by
  // unmounting would shift the header once auth resolves.
  const showLogin = isAuthResolved && !isSignedIn;
  const discountPercent = startingPrice?.discountPercent ?? DEFAULT_DISCOUNT_PERCENT;

  return (
    <header className="sticky top-0 z-50 border-b border-pi-border bg-pi-bg">
      <div className="mx-auto flex min-h-16 max-w-[1200px] items-center justify-between gap-3 px-4 py-2 sm:px-6 lg:px-8">
        <Link
          href="/#top"
          aria-label="Pi by Physics Wallah"
          className="flex shrink-0 items-center leading-none"
        >
          <PiLogo idPrefix="header" height={38} />
        </Link>
        <div className="flex min-w-0 items-center gap-3 sm:gap-4 lg:gap-6">
          <AuthButton
            appearance="plain"
            preselectPlan={false}
            aria-hidden={!showLogin}
            tabIndex={showLogin ? undefined : -1}
            loginEntryPoint={PI_LOGIN_ENTRY_POINT.HEADER_LOGIN}
            trackClick={() =>
              piEvents.ctaClicked({
                ctaName: PI_CTA_NAME.LOGIN,
                ctaLocation: PI_CTA_LOCATION.HEADER,
                destination: PI_DESTINATION.LOGIN_MODAL,
              })
            }
            className={`inline min-w-0 cursor-pointer border-0 bg-transparent p-0 text-left text-sm font-semibold break-words text-white transition-[color,opacity] hover:text-pi-accent-soft disabled:opacity-50 sm:text-[15px] ${
              showLogin ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            Login
          </AuthButton>
          <AuthButton
            size="small"
            variant="gold"
            loginEntryPoint={PI_LOGIN_ENTRY_POINT.HEADER_START_LEARNING}
            trackClick={() =>
              piEvents.ctaClicked({
                ctaName: PI_CTA_NAME.START_LEARNING_FREE,
                ctaLocation: PI_CTA_LOCATION.HEADER,
                destination: isSignedIn
                  ? PI_DESTINATION.PW_HOME
                  : PI_DESTINATION.LOGIN_MODAL,
              })
            }
            className="min-w-0 px-3 text-center text-[13px] font-bold break-words sm:px-[18px] sm:text-sm"
          >
            {HEADER_COPY.ctaPrefix} {discountPercent}% {HEADER_COPY.ctaSuffix}
          </AuthButton>
        </div>
      </div>

      <div className="border-t border-[#6b4f08] bg-gradient-to-r from-[#2a1d03] via-[#4a3406] to-[#2a1d03]">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-center gap-2 px-4 py-2 sm:gap-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.08em] text-pi-gold uppercase sm:text-xs">
            <span className="size-[7px] shrink-0 animate-pulse rounded-full bg-[#FF5A3C]" />
            {discountPercent}% OFF ends in
          </span>
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            <TimeBox value={countdown.hours} label="HRS" />
            <span className="font-bold text-[#C9A94A]">:</span>
            <TimeBox value={countdown.minutes} label="MIN" />
            <span className="font-bold text-[#C9A94A]">:</span>
            <TimeBox value={countdown.seconds} label="SEC" />
          </span>
          <span className="hidden text-[13px] text-[#E8D79B] sm:inline">
            Uske baad {startingPrice?.originalPrice ?? DEFAULT_ORIGINAL_PRICE}
            {startingPrice?.suffix ?? "/month"}
          </span>
        </div>
      </div>
    </header>
  );
}
